import {Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Subscription} from 'rxjs';

@Component({
    selector: 'hmt-container-number',
    templateUrl: './container-number.component.html',
    styleUrls: ['./container-number.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ContainerNumberComponent implements OnInit, OnChanges {

  form: FormGroup;
  retypeForm: FormGroup;
  onFormChange: Subscription;
  onRetypeFormChange: Subscription;
  formValuesList: string[];
  retypeFormValuesList: string[];
  @Input() containerNumber: string; // Input to receive existing container number
  @Output() validateContainerNumber: EventEmitter<string> = new EventEmitter<string>();
  @ViewChild('input11') lastInputRefOfMainForm: ElementRef;
  @ViewChild('reInput11') lastInputRefOfRetypeFormForm: ElementRef;

  constructor(
    private formBuilder: FormBuilder
  ) {
  }

  ngOnInit(): void {
    this.createForm();
    this.createRetypeForm();
    
    // If container number is provided initially, populate both forms
    if (this.containerNumber && this.containerNumber.length === 11) {
      this.populateFormsWithContainerNumber(this.containerNumber);
    }
    
    // Listen to main form changes
    this.onFormChange = this.form.valueChanges
      .subscribe(() => {
        this.validateAndEmitContainerNumber();
      });
    
    // Listen to retype form changes
    this.onRetypeFormChange = this.retypeForm.valueChanges
      .subscribe(() => {
        this.validateAndEmitContainerNumber();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Detect when containerNumber input changes (including first change if forms are ready)
    if (changes['containerNumber'] && changes['containerNumber'].currentValue) {
      const containerNumber = changes['containerNumber'].currentValue;
      // Check if forms are initialized before populating
      if (this.form && this.retypeForm && containerNumber.length === 11) {
        this.populateFormsWithContainerNumber(containerNumber);
      }
    }
  }

  // Populate both forms with existing container number
  populateFormsWithContainerNumber(containerNumber: string) {
    if (containerNumber.length !== 11) {
      return;
    }

    // Populate main form (without emitting events to avoid multiple emissions)
    this.form.get('ownerCode').get('ownerCodeCharOne').setValue(containerNumber[0], {emitEvent: false});
    this.form.get('ownerCode').get('ownerCodeCharTwo').setValue(containerNumber[1], {emitEvent: false});
    this.form.get('ownerCode').get('ownerCodeCharThree').setValue(containerNumber[2], {emitEvent: false});
    this.form.get('category').setValue(containerNumber[3], {emitEvent: false});
    this.form.get('serialNumber').get('digitOne').setValue(containerNumber[4], {emitEvent: false});
    this.form.get('serialNumber').get('digitTwo').setValue(containerNumber[5], {emitEvent: false});
    this.form.get('serialNumber').get('digitThree').setValue(containerNumber[6], {emitEvent: false});
    this.form.get('serialNumber').get('digitFour').setValue(containerNumber[7], {emitEvent: false});
    this.form.get('serialNumber').get('digitFive').setValue(containerNumber[8], {emitEvent: false});
    this.form.get('serialNumber').get('digitSix').setValue(containerNumber[9], {emitEvent: false});
    this.form.get('checkDigit').setValue(containerNumber[10], {emitEvent: false});

    // Populate retype form (without emitting events to avoid multiple emissions)
    this.retypeForm.get('ownerCode').get('ownerCodeCharOne').setValue(containerNumber[0], {emitEvent: false});
    this.retypeForm.get('ownerCode').get('ownerCodeCharTwo').setValue(containerNumber[1], {emitEvent: false});
    this.retypeForm.get('ownerCode').get('ownerCodeCharThree').setValue(containerNumber[2], {emitEvent: false});
    this.retypeForm.get('category').setValue(containerNumber[3], {emitEvent: false});
    this.retypeForm.get('serialNumber').get('digitOne').setValue(containerNumber[4], {emitEvent: false});
    this.retypeForm.get('serialNumber').get('digitTwo').setValue(containerNumber[5], {emitEvent: false});
    this.retypeForm.get('serialNumber').get('digitThree').setValue(containerNumber[6], {emitEvent: false});
    this.retypeForm.get('serialNumber').get('digitFour').setValue(containerNumber[7], {emitEvent: false});
    this.retypeForm.get('serialNumber').get('digitFive').setValue(containerNumber[8], {emitEvent: false});
    this.retypeForm.get('serialNumber').get('digitSix').setValue(containerNumber[9], {emitEvent: false});
    this.retypeForm.get('checkDigit').setValue(containerNumber[10], {emitEvent: false});

    // Manually trigger validation and emit the container number
    this.validateAndEmitContainerNumber();
  }

  // Validate and emit container number or empty string
  validateAndEmitContainerNumber() {
    if (this.form.valid && this.retypeForm.valid && this.matchingWithRetypeForm()) {
      const containerNumber = this.convertFormValueArrayToString(this.getValuesAsArray(this.form.value));
      this.validateContainerNumber.emit(containerNumber);
    } else {
      // Emit empty string when invalid or not matching
      this.validateContainerNumber.emit('');
    }
  }

  createForm() {
    this.form = this.formBuilder.group({
      ownerCode: new FormGroup({
        ownerCodeCharOne: this.createFormControl('[A-Za-z]+'),
        ownerCodeCharTwo: this.createFormControl('[A-Za-z]+'),
        ownerCodeCharThree: this.createFormControl('[A-Za-z]+')
      }),
      category: this.createFormControl('[UJZujz]+'),
      serialNumber: new FormGroup({
        digitOne: this.createFormControl('[0-9]'),
        digitTwo: this.createFormControl('[0-9]'),
        digitThree: this.createFormControl('[0-9]'),
        digitFour: this.createFormControl('[0-9]'),
        digitFive: this.createFormControl('[0-9]'),
        digitSix: this.createFormControl('[0-9]'),
      }),
      checkDigit: this.createFormControl('[0-9]')
    });
  }

  createRetypeForm() {
    this.retypeForm = this.formBuilder.group({
      ownerCode: new FormGroup({
        ownerCodeCharOne: this.createFormControl(),
        ownerCodeCharTwo: this.createFormControl(),
        ownerCodeCharThree: this.createFormControl(),
      }),
      category: this.createFormControl(),
      serialNumber: new FormGroup({
        digitOne: this.createFormControl(),
        digitTwo: this.createFormControl(),
        digitThree: this.createFormControl(),
        digitFour: this.createFormControl(),
        digitFive: this.createFormControl(),
        digitSix: this.createFormControl(),
      }),
      checkDigit: this.createFormControl()
    });
  }

  createFormControl(pattern?: string): FormControl {
    return pattern ? new FormControl('', [Validators.required, Validators.pattern(pattern)])
      : new FormControl('', [Validators.required]);
  }

  matchingWithRetypeForm(): boolean {
    if (this.form.valid) {
      const formValues = this.getValuesAsArray(this.form.value);
      const reTypeformValues = this.getValuesAsArray(this.retypeForm.value);
      // Case-insensitive comparison
      const misMatch = formValues.find(((value, index) => {
        const val1 = (value || '').toUpperCase();
        const val2 = (reTypeformValues[index] || '').toUpperCase();
        return val1 !== val2;
      }));
      return !misMatch;
    }
  }

  getValuesAsArray(values): string[] {
    const arrayOfFormValues = [];
    arrayOfFormValues.push(values.ownerCode.ownerCodeCharOne);
    arrayOfFormValues.push(values.ownerCode.ownerCodeCharTwo);
    arrayOfFormValues.push(values.ownerCode.ownerCodeCharThree);
    arrayOfFormValues.push(values.category);
    arrayOfFormValues.push(values.serialNumber.digitOne);
    arrayOfFormValues.push(values.serialNumber.digitTwo);
    arrayOfFormValues.push(values.serialNumber.digitThree);
    arrayOfFormValues.push(values.serialNumber.digitFour);
    arrayOfFormValues.push(values.serialNumber.digitFive);
    arrayOfFormValues.push(values.serialNumber.digitSix);
    arrayOfFormValues.push(values.checkDigit);
    return arrayOfFormValues;
  }

  convertFormValueArrayToString(values: string[]): string {
    let containerNumber = '';
    values.forEach((val) => {
      containerNumber = containerNumber + val;
    });
    // Always return uppercase for consistency
    return containerNumber.toUpperCase();
  }

  isReTypeFormValid() {
    return this.retypeForm.valid;
  }

  isFormInValid() {
    if (this.form.dirty) {
      return this.form.invalid;
    }
  }

  // Check if a specific retype field matches the main form field
  isRetypeFieldMismatch(mainPath: string, retypePath: string): boolean {
    // Only show mismatch if retype form has been touched
    const retypeControl = this.retypeForm.get(retypePath);
    if (!retypeControl || !retypeControl.value || !retypeControl.touched) {
      return false;
    }
    
    const mainValue = (this.form.get(mainPath)?.value || '').toUpperCase();
    const retypeValue = (retypeControl.value || '').toUpperCase();
    
    // Case-insensitive comparison
    return mainValue !== retypeValue;
  }

  onPasteFirstInputOfMainForm(event: ClipboardEvent) {
    event.preventDefault(); // Prevent default paste behavior
    
    const clipboardData = event.clipboardData;
    let pastedText = clipboardData.getData('text');
    
    // Clean the pasted text: remove spaces, dashes, and trim
    pastedText = pastedText.replace(/[\s-]/g, '').trim().toUpperCase();
    
    // Check if it's exactly 11 characters
    if (pastedText.length !== 11) {
      console.warn('Invalid container number length. Expected 11 characters, got:', pastedText.length);
      return;
    }
    
    // Validate format (3 letters + 1 letter + 6 digits + 1 digit)
    const containerRegex = /^[A-Z]{3}[UJZ][0-9]{6}[0-9]$/;
    if (!containerRegex.test(pastedText)) {
      console.warn('Invalid container number format:', pastedText);
      return;
    }
    
    // Fill all fields
    this.form.get('ownerCode').get('ownerCodeCharOne').patchValue(pastedText[0]);
    this.form.get('ownerCode').get('ownerCodeCharTwo').patchValue(pastedText[1]);
    this.form.get('ownerCode').get('ownerCodeCharThree').patchValue(pastedText[2]);
    this.form.get('category').patchValue(pastedText[3]);
    this.form.get('serialNumber').get('digitOne').patchValue(pastedText[4]);
    this.form.get('serialNumber').get('digitTwo').patchValue(pastedText[5]);
    this.form.get('serialNumber').get('digitThree').patchValue(pastedText[6]);
    this.form.get('serialNumber').get('digitFour').patchValue(pastedText[7]);
    this.form.get('serialNumber').get('digitFive').patchValue(pastedText[8]);
    this.form.get('serialNumber').get('digitSix').patchValue(pastedText[9]);
    this.form.get('checkDigit').patchValue(pastedText[10]);
    
    // Mark all as touched to trigger validation display
    this.form.markAllAsTouched();
    
    // Focus the last input
    this.lastInputRefOfMainForm.nativeElement.focus();
  }

  onPasteFirstInputOfRetypeForm(event: ClipboardEvent) {
    event.preventDefault(); // Prevent default paste behavior
    
    const clipboardData = event.clipboardData;
    let pastedText = clipboardData.getData('text');
    
    // Clean the pasted text: remove spaces, dashes, and trim
    pastedText = pastedText.replace(/[\s-]/g, '').trim().toUpperCase();
    
    // Check if it's exactly 11 characters
    if (pastedText.length !== 11) {
      console.warn('Invalid container number length. Expected 11 characters, got:', pastedText.length);
      return;
    }
    
    // Validate format (3 letters + 1 letter + 6 digits + 1 digit)
    const containerRegex = /^[A-Z]{3}[UJZ][0-9]{6}[0-9]$/;
    if (!containerRegex.test(pastedText)) {
      console.warn('Invalid container number format:', pastedText);
      return;
    }
    
    // Fill all fields
    this.retypeForm.get('ownerCode').get('ownerCodeCharOne').patchValue(pastedText[0]);
    this.retypeForm.get('ownerCode').get('ownerCodeCharTwo').patchValue(pastedText[1]);
    this.retypeForm.get('ownerCode').get('ownerCodeCharThree').patchValue(pastedText[2]);
    this.retypeForm.get('category').patchValue(pastedText[3]);
    this.retypeForm.get('serialNumber').get('digitOne').patchValue(pastedText[4]);
    this.retypeForm.get('serialNumber').get('digitTwo').patchValue(pastedText[5]);
    this.retypeForm.get('serialNumber').get('digitThree').patchValue(pastedText[6]);
    this.retypeForm.get('serialNumber').get('digitFour').patchValue(pastedText[7]);
    this.retypeForm.get('serialNumber').get('digitFive').patchValue(pastedText[8]);
    this.retypeForm.get('serialNumber').get('digitSix').patchValue(pastedText[9]);
    this.retypeForm.get('checkDigit').patchValue(pastedText[10]);
    
    // Mark all as touched to trigger validation display
    this.retypeForm.markAllAsTouched();
    
    // Focus the last input
    this.lastInputRefOfRetypeFormForm.nativeElement.focus();
  }

  // Handle copy functionality - allows selecting all boxes and copying
  onCopyMainForm(event: ClipboardEvent) {
    if (this.form.valid) {
      event.preventDefault();
      const containerNumber = this.convertFormValueArrayToString(this.getValuesAsArray(this.form.value));
      event.clipboardData.setData('text/plain', containerNumber);
    }
  }

  onCopyRetypeForm(event: ClipboardEvent) {
    if (this.retypeForm.valid) {
      event.preventDefault();
      const containerNumber = this.convertFormValueArrayToString(this.getValuesAsArray(this.retypeForm.value));
      event.clipboardData.setData('text/plain', containerNumber);
    }
  }

  // Enhanced backspace functionality - moves to previous box when current is empty
  onKeyDown(event: KeyboardEvent, currentInput: HTMLInputElement, previousInput?: HTMLInputElement) {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      const value = currentInput.value;
      
      // If current box is empty and backspace is pressed, move to previous box
      if (!value && event.key === 'Backspace' && previousInput) {
        event.preventDefault();
        previousInput.focus();
        // Clear the previous box value
        previousInput.value = '';
        // Trigger change detection
        previousInput.dispatchEvent(new Event('input'));
      }
      // If current box has value and backspace, just clear it (default behavior)
      else if (value && event.key === 'Backspace') {
        // Let default behavior clear the value
        // Auto-tab back will happen via the input event
      }
      // If Delete key, clear current box but stay in place
      else if (value && event.key === 'Delete') {
        event.preventDefault();
        currentInput.value = '';
        currentInput.dispatchEvent(new Event('input'));
      }
    }
  }
}


